import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Brain, ArrowRight, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { fmtNum } from "@/lib/format";

// The "WHY?" prescriptive card. Shows deterministic root cause + an LLM-narrated
// explanation grounded on the same numbers.
export function WhyCard({ why }) {
  const [insight, setInsight] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.aiInsight()
      .then((d) => { if (alive) setInsight(d.insight); })
      .catch(() => { if (alive) setInsight(""); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // refresh narration on mount only; numbers below update live
  }, []);

  if (!why) return null;

  return (
    <div className="panel relative overflow-hidden h-full" data-testid="why-card">
      <div className="absolute inset-0 pointer-events-none"
        style={{ background: "radial-gradient(120% 120% at 80% 0%, rgba(51,183,109,0.10), transparent 60%), linear-gradient(180deg, rgba(10,18,36,0.55), rgba(5,9,20,0.92))" }} />

      <div className="relative p-5">
        <div className="flex items-center gap-2 mb-3">
          <Brain size={16} className="text-brand-green" />
          <span className="eyebrow" style={{ color: "#33b76d" }}>AI Insight · Why?</span>
          <span className="ml-auto text-[0.62rem] font-mono px-2 py-0.5 border border-brand-green/40 text-brand-green uppercase tracking-wider">Llama 3</span>
        </div>

        <div className="flex items-end gap-2">
          <div className="text-5xl font-bold tnum text-brand-gold leading-none">{fmtNum(why.probability)}%</div>
          <div className="pb-1">
            <div className="text-sm font-display font-bold uppercase tracking-tight text-ink">{why.primary_cause}</div>
            <div className="text-[0.7rem] text-ink-soft">probability · root cause</div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-2 mt-4">
          <Metric label="Current Moisture" value={`${fmtNum(why.current_moisture, 1)}%`} color="#fab20b" />
          <Metric label="Historical" value={`${fmtNum(why.historical_moisture, 1)}%`} color="#8BA1C0" />
          <Metric label="Impact" value={`+${fmtNum(why.expected_impact_mt, 1)} MT`} color="#E63946" />
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }}
          className="mt-4 text-[0.82rem] leading-relaxed text-ink/90 min-h-[64px]">
          {loading ? (
            <span className="text-ink-soft inline-flex items-center gap-2">
              <Sparkles size={13} className="animate-pulse text-brand-green" /> Analysing plant telemetry…
            </span>
          ) : (insight || `Coal moisture has risen to ${fmtNum(why.current_moisture,1)}% versus a historical ${fmtNum(why.historical_moisture,1)}%, eroding effective GCV and boiler efficiency.`)}
        </motion.div>

        <div className="mt-4 panel-2 px-3.5 py-3 flex items-center gap-3 border-l-2" style={{ borderLeftColor: "#33b76d" }}>
          <ArrowRight size={16} className="text-brand-green shrink-0" />
          <div>
            <div className="eyebrow" style={{ color: "#33b76d" }}>Recommended Action</div>
            <div className="text-sm font-medium text-ink mt-0.5">{why.recommended_action}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, color }) {
  return (
    <div className="panel-2 px-2.5 py-2">
      <div className="eyebrow truncate">{label}</div>
      <div className="text-base font-bold tnum mt-0.5" style={{ color }}>{value}</div>
    </div>
  );
}
